import { Dispatch, SetStateAction } from 'react';

import {
  CreditCardOutlined,
  EditOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { T } from 'react-translator-component';

import { CircleNFEStatus, IconArea } from './styled';

import { ReactComponent as PixIcon } from '../../../assets/pix.svg';
import ticketLogLogo from '../../../assets/ticket-log-logo.png';
import {
  ITransaction,
  NFEStatus,
  PaymentTypes,
} from '../../../types/responses/Transactions';
import { hasFinancialIndicators } from '../../../utils/hasFinancialIndicators/hasFinancialIndicators';
import { isMonetizationProUser } from '../../../utils/isMonetizationProUser/isMonetizationProUser';
import { isTerpelTenant } from '../../../utils/isTerpelTenant/isTerpelTenant';
import {
  getPaymentSituation,
  PaymentSituation,
} from '../../../utils/paymentSituation/paymentSituation';
import { COLORS } from '../../../utils/theme/colors';
import { CoinIcon } from '../../atoms/icons/CoinIcon';
import { ConfirmationNumber } from '../../atoms/icons/ConfirmationNumber';
import { TenantLogoIcon } from '../../atoms/icons/TenantLogoIcon';
import { ThunderIcon } from '../../atoms/icons/ThunderIcon';
import { OriginBadge } from '../../atoms/OriginBadge/OriginBadge';
import { ResendNFEModalProps } from '../../pages/TransactionList/TransactionList';

const MPT = ['Sem nome', T('Undefined')];

// MV2-1802: a cor do valor sai da mesma regra que o filtro PAID/UNPAID do CS usa -- antes eram duas
// implementacoes e elas discordavam. A recarga 83627 de test1 saiu PAGA no app e PENDENTE no
// dashboard por causa disso. Ver utils/paymentSituation.
const amountStatusColor = (transaction: ITransaction) => {
  switch (getPaymentSituation(transaction)) {
    case PaymentSituation.PAID:
      return COLORS.amountStatusPaid;
    case PaymentSituation.UNPAID:
      return COLORS.amountStatusNotPaid;
    default:
      return COLORS.amountStatusInformation;
  }
};

const innerDataFormater = (transaction: ITransaction) => {
  return {
    transactionId: transaction.id,
    station: transaction.chargeBoxDescription || 'Sem nome',
    autonomy: transaction.autonomy,
    chargeBoxPk: transaction.chargeBoxPk,
    connectorPk: transaction.connectorPk,
    stopReason: transaction.stopReason,
    feedbackStars: transaction.feedbackStars,
    feedbackDescription: transaction.feedbackDescription,
    idleDuration: transaction.idleDuration,
    moneyIdlePaymentTransactionAmount:
      transaction.moneyIdlePaymentTransactionAmount,
    idleDurationHumanReadable: transaction.idleDurationHumanReadable,
    moneyIdlePaymentTransactionAmountHumanReadable:
      transaction.moneyIdlePaymentTransactionAmountHumanReadable,
    stopEvent: transaction.stopEventActor,
    stopTimestamp: transaction.stopTimestamp,
    durationTime: transaction.durationTime,
    userEmail: transaction.userEmail,
    userPhone: transaction.userPhone,
  };
};

const iconMappings = {
  [PaymentTypes.MERCADO_PAGO_CARD]: {
    icon: <CreditCardOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
    additionalIcon: (
      <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
    ),
  },
  [PaymentTypes.PAGBANK_CARD]: {
    icon: <CreditCardOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
    additionalIcon: (
      <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
    ),
  },
  [PaymentTypes.MERCADO_PAGO_PIX]: {
    icon: <PixIcon />,
    additionalIcon: (
      <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
    ),
  },
  [PaymentTypes.WALLET]: {
    icon: <WalletOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
    additionalIcon: (
      <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
    ),
  },
  [PaymentTypes.MANUAL_DASHBOARD]: {
    icon: <EditOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
  },
  [PaymentTypes.VOUCHER]: {
    icon: <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />,
  },
  [PaymentTypes.TICKET_LOG]: {
    icon: (
      <img src={ticketLogLogo} alt='Ticket Log Logo' width={37} height={32} />
    ),
    additionalIcon: (
      <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
    ),
  },
};

const getTransactionIcon = (
  transactionPaymentType: PaymentTypes,
  voucherUnitPk?: number,
) => {
  const mapping = iconMappings[transactionPaymentType];

  if (!mapping) {
    return null;
  }

  const { icon, additionalIcon } = mapping;

  if (voucherUnitPk) {
    return (
      <IconArea>
        {icon}
        {additionalIcon}
      </IconArea>
    );
  }

  return icon;
};

const getNfeStatusColor = (nfeStatus: NFEStatus | null) => {
  if (!nfeStatus) {
    return COLORS.white;
  }

  switch (nfeStatus) {
    case NFEStatus.ERROR_DISABLE_OR_NOT_CONFIGURED:
    case NFEStatus.ERRO_CANCELAMENTO:
    case NFEStatus.ERROR_CS:
      return COLORS.red;
    case NFEStatus.PROCESSANDO_AUTORIZACAO:
      return COLORS.stone;
    default:
      return COLORS.popular;
  }
};

const isMinEnergyLimitApplied = (transaction: ITransaction) =>
  transaction?.paymentStatus === 'cancelled' &&
  transaction?.paymentStatusDetail === 'cancelled_energy_limit_min';

const listFormater = (
  list: ITransaction[],
  shouldShowNFColumn: boolean,
  setShowResendNFEModal: Dispatch<SetStateAction<ResendNFEModalProps>>,
) => {
  let cardsList: any = [];
  let innerData: any = [];

  list !== undefined &&
    list.forEach((transaction) => {
      const minEnergyLimitApplied = isMinEnergyLimitApplied(transaction);

      cardsList = [
        ...cardsList,
        [
          {
            value: transaction.id || MPT[0],
            subValue: '',
            icon: <></>,
            size: '7%',
          },
          {
            value: transaction.chargeBoxDescription || MPT[0],
            subValue: transaction.chargeBoxId,
            icon: (
              <TenantLogoIcon
                size={30}
                logoIconUrl={transaction?.stationTenantAlias}
                tenantAlias={transaction?.stationTenantAlias}
              />
            ),
            size: '13%',
          },
          {
            value: transaction.connectorId || MPT[1],
            subValue: '',
            icon: <></>,
            size: '5%',
          },
          {
            value: transaction.addressState || '-',
            subValue: '',
            icon: <></>,
            size: '5%',
          },
          {
            value: transaction.addressCity || '-',
            subValue: '',
            icon: <></>,
            size: '9%',
          },
          {
            value: (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span>
                  {transaction.userName != null
                    ? transaction.userName +
                      (transaction.userCompany
                        ? `/ ${transaction.userCompany}`
                        : '')
                    : transaction.ocppIdTag || MPT[1]}
                </span>
                <OriginBadge origin={transaction.origin} />
              </span>
            ),
            subValue: '',
            icon: (
              <TenantLogoIcon
                size={30}
                logoIconUrl={transaction?.userTenantAlias}
                tenantAlias={transaction?.userTenantAlias}
              />
            ),
            size: '12%',
          },
          {
            value: `${
              transaction.startTimestamp != null
                ? transaction.startTimestamp.replace(/.{3}$/, '')
                : ''
            } - ${
              transaction.stopTimestamp != null
                ? transaction.stopTimestamp.replace(/.{3}$/, '')
                : T('Charging') + '...'
            }`,
            subValue: '',
            icon: <></>,
            size: '15%',
          },
          {
            value: transaction.durationHumanReadable,
            subValue: '',
            size: '7%',
            icon: <></>,
          },
          shouldShowNFColumn &&
            (isMonetizationProUser() || isTerpelTenant()) && {
              icon: (
                <CircleNFEStatus
                  backgroundColor={getNfeStatusColor(transaction.nfeStatus)}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      getNfeStatusColor(transaction.nfeStatus) === COLORS.red
                    ) {
                      setShowResendNFEModal({
                        visible: true,
                        transactionPk: transaction.id,
                      });
                    }
                  }}
                />
              ),
              size: '5%',
            },
          ...(isMonetizationProUser() || isTerpelTenant()
            ? [
                {
                  icon: getTransactionIcon(
                    transaction.paymentType,
                    transaction?.voucherUnitPk,
                  ),
                  size: '3%',
                },
              ]
            : []),
          ...((isMonetizationProUser() || isTerpelTenant()) &&
          hasFinancialIndicators()
            ? [
                {
                  value: minEnergyLimitApplied
                    ? 'R$ 0,00'
                    : transaction.transactionAmount
                    ? transaction.transactionAmountAvailableHumanReadable
                    : transaction.incomeHumanReadable,
                  subValue: '',
                  size: '7%',
                  textColor: amountStatusColor(transaction),
                  icon: (
                    <CoinIcon
                      size={20}
                      color={amountStatusColor(transaction)}
                    />
                  ),
                },
              ]
            : []),
          {
            value: transaction.energyHumanReadable,
            subValue: '',
            size: '6%',
            icon: <ThunderIcon size={20} />,
          },
        ],
      ];
      innerData = [...innerData, innerDataFormater(transaction)];
    });

  return { cardsList, innerData };
};

export const combiningObjects = (
  list: ITransaction[],
  shouldShowNFColumn,
  header,
  setShowResendNFEModal: Dispatch<SetStateAction<ResendNFEModalProps>>,
) => {
  return {
    header,
    ...listFormater(list, shouldShowNFColumn, setShowResendNFEModal),
  };
};
